import React, { useState, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { apiClient } from "../../api/client";
import { useApiRequest } from "../../api";
import {
  setNotifications,
  setVoiceReports,
  setSilentMode,
  setSummaryEnabled,
  setSummaryFrequency,
  setSimplifiedMapRendering,
} from "../../store/slices/settingsSlice";
import {
  setSelectedCity,
  setEvents,
  setWebcams,
} from "../../store/slices/mapSlice";
import { CitySelector } from "../CitySelector/CitySelector";
//import { FALLBACK_CITIES } from "../../data/cities";
import styles from "./Settings.module.css";
import { useTheme } from "../../hooks/useTheme";
import type { City } from "../../types";
import { Toggle } from "../Toggle/Toggle";
import { Portal } from "../Portal/Portal";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { useBackButton } from "../../hooks/useBackButton";
interface SettingsProps {
  onClose: () => void;
  onCitySelect: (city: City) => void;
}

export const Settings: React.FC<SettingsProps> = ({
  onClose,
  onCitySelect,
}) => {
  const dispatch = useAppDispatch();
  const { selectedCity } = useAppSelector((state) => state.map);
  const settings = useAppSelector((state) => state.settings);
  const { request } = useApiRequest();
  const { theme, toggleTheme } = useTheme();

  // Состояние экранов
  const [showSummaryScreen, setShowSummaryScreen] = useState(false);
  const [showCitySelector, setShowCitySelector] = useState(false);

  const handleMainBack = useCallback(() => {
    HapticFeedback.light();
    onClose();
  }, [onClose]);

  const handleSummaryBack = useCallback(() => {
    HapticFeedback.light();
    setShowSummaryScreen(false);
  }, []);

  const handleCitySelectorBack = useCallback(() => {
    HapticFeedback.light();
    setShowCitySelector(false);
  }, []);

  const getBackButtonConfig = () => {
    if (showCitySelector) {
      return { isOpen: true, onBack: handleCitySelectorBack };
    }
    if (showSummaryScreen) {
      return { isOpen: true, onBack: handleSummaryBack };
    }
    return { isOpen: true, onBack: handleMainBack };
  };

  const config = getBackButtonConfig();

  useBackButton({
    isOpen: config.isOpen,
    onBack: config.onBack,
  });

  // Сохраняем настройки на бекенд + обновляем Redux
  const saveSetting = async (
    key: string,
    value: string,
    reduxAction: () => void
  ) => {
    try {
      reduxAction();
      await request(() => apiClient.saveSettings(key, value));
      HapticFeedback.success(); // Успешное сохранение настроек
      console.log(`✅ Настройка ${key} сохранена:`, value);
    } catch (error) {
      HapticFeedback.error(); // Ошибка сохранения
      console.error(`❌ Ошибка сохранения настройки ${key}:`, error);
    }
  };

  const handleNotificationsToggle = () => {
    const newValue = !settings.notifications;
    saveSetting("instant-notifications", newValue.toString(), () =>
      dispatch(setNotifications(newValue))
    );
  };

  const handleVoiceReportsToggle = () => {
    const newValue = !settings.voiceReports;
    saveSetting("voice-reports", newValue.toString(), () =>
      dispatch(setVoiceReports(newValue))
    );
  };

  const handleSilentModeToggle = () => {
    const newValue = !settings.silentMode;
    saveSetting("quiet-mode", newValue.toString(), () =>
      dispatch(setSilentMode(newValue))
    );
  };

  const handleDarkThemeToggle = () => {
    toggleTheme();
  };

  const handleSimplifiedMapRenderingToggle = () => {
    const newValue = !settings.simplifiedMapRendering;
    dispatch(setSimplifiedMapRendering(newValue));
    HapticFeedback.selectionChanged();
  };

  const handleSummaryToggle = async () => {
    const newValue = !settings.summaryEnabled;

    await saveSetting("summary_enabled", newValue.toString(), () =>
      dispatch(setSummaryEnabled(newValue))
    );

    if (newValue) {
      await saveSetting("digest-frequency", "60", () =>
        dispatch(setSummaryFrequency("60"))
      );
      setShowSummaryScreen(true);
    } else {
      await saveSetting("digest-frequency", "-1", () =>
        dispatch(setSummaryFrequency("-1"))
      );
    }
  };

  const handleFrequencySelect = (frequency: string) => {
    HapticFeedback.selectionChanged();
    saveSetting("digest-frequency", frequency, () =>
      dispatch(setSummaryFrequency(frequency))
    );
    setShowSummaryScreen(false);
  };

  // Обработчик выбора города
  const handleCitySelect = async (city: City) => {
    setShowCitySelector(false);

    HapticFeedback.light();

    try {
      console.log("🏙️ Выбран город:", city.name);

      dispatch(setEvents({ events: [] }));
      dispatch(setWebcams([]));
      dispatch(setSelectedCity(city));

      await onCitySelect(city);

      Promise.all([
        request(() => apiClient.saveCity(city.id)),
        request(apiClient.getAppData),
      ])
        .then(([, data]) => {
          if (data) {
            const shouldSetEvents =
              data._cities?.find((c) => c.id === city.id)?.settings
                ?.is_mailing_enabled !== false;

            if (shouldSetEvents) {
              dispatch(
                setEvents({
                  events: data.events || [],
                  isMailingEnabled: true,
                })
              );
            } else {
              dispatch(
                setEvents({
                  events: [],
                  isMailingEnabled: false,
                })
              );
            }

            dispatch(setWebcams(data.webcams || []));
          }
        })
        .catch((error) => {
          console.error("❌ Ошибка сохранения города в фоне:", error);
        });
    } catch (error) {
      console.error("❌ Ошибка выбора города:", error);
    }
  };

  // Открыть селектор города
  const handleOpenCitySelector = () => {
    HapticFeedback.light();
    setShowCitySelector(true);
  };

  // Селектор города
  if (showCitySelector) {
    return (
      <Portal>
        <CitySelector
          selectedCity={selectedCity}
          onCitySelect={handleCitySelect}
          isOpen={showCitySelector}
          onClose={() => setShowCitySelector(false)}
          loading={false}
        />
      </Portal>
    );
  }

  // Экран выбора частоты рассылки
  if (showSummaryScreen) {
    return (
      <Portal>
        <div className={styles.overlay}>
          <div
            className={`${styles.summaryScreen} ${
              theme === "dark" ? styles.darkTheme : ""
            }`}
          >
            <h1 className={styles.summaryTitle}>Рассылка сводки</h1>

            <div className={styles.summaryToggleSection}>
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <div className={styles.settingTitle}>Получать сводки</div>
                  <div className={styles.settingSubtitle}>
                    Пришлём сводки о ситуации на дорогах в удобное для вас время
                  </div>
                </div>
                <Toggle
                  isActive={settings.summaryEnabled}
                  onClick={handleSummaryToggle}
                  theme={theme}
                />
              </div>
            </div>

            {settings.summaryEnabled && (
              <>
                <div className={styles.frequencyTitle}>
                  Как часто присылать сводку?
                </div>
                <div className={styles.frequencyOptions}>
                  {[
                    { value: "15", label: "Каждые 15 минут" },
                    { value: "30", label: "Каждые 30 минут" },
                    { value: "60", label: "Каждый час" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      className={`${styles.frequencyOption} ${
                        settings.summaryFrequency === option.value
                          ? styles.selected
                          : ""
                      }`}
                      onClick={() => handleFrequencySelect(option.value)}
                    >
                      <span>{option.label}</span>
                      {settings.summaryFrequency === option.value && (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Portal>
    );
  }

  // Основной экран настроек
  return (
    <Portal>
      <div className={styles.overlay}>
        <div
          className={`${styles.container} ${
            theme === "dark" ? styles.darkTheme : ""
          }`}
          data-theme={theme}
        >
          <h1 className={styles.title}>Настройки</h1>

          {/* Город */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Город</div>
            <button
              className={styles.cityButton}
              onClick={handleOpenCitySelector}
            >
              <span className={styles.cityName}>
                {selectedCity?.name || "Не выбран"}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Рассылка сводки */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Рассылка сводки</div>
            <button
              className={styles.summaryButton}
              onClick={() => {
                HapticFeedback.light();
                setShowSummaryScreen(true);
              }}
            >
              <span className={styles.summaryFrequencyText}>
                {settings.summaryEnabled
                  ? `Каждые ${settings.summaryFrequency} минут`
                  : "Отключены"}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Переключатели */}
          <div className={styles.togglesSection}>
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingTitle}>Уведомления</div>
                <div className={styles.settingSubtitle}>
                  Моментальные пуши о событиях
                </div>
              </div>
              <Toggle
                isActive={settings.notifications}
                onClick={handleNotificationsToggle}
                theme={theme}
              />
            </div>

            <div className={styles.separator}></div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingTitle}>Голосовые сводки</div>
                <div className={styles.settingSubtitle}>
                  Аудиоуведомления о ситуации на дорогах
                </div>
              </div>
              <Toggle
                isActive={settings.voiceReports}
                onClick={handleVoiceReportsToggle}
                theme={theme}
              />
            </div>

            <div className={styles.separator}></div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingTitle}>Режим тишины</div>
                <div className={styles.settingSubtitle}>
                  Не будем отправлять уведомления с 22:00 до 08:00
                </div>
              </div>
              <Toggle
                isActive={settings.silentMode}
                onClick={handleSilentModeToggle}
                theme={theme}
              />
            </div>

            <div className={styles.separator}></div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingTitle}>Тёмная тема</div>
              </div>
              <Toggle
                isActive={theme === "dark"}
                onClick={handleDarkThemeToggle}
                theme={theme}
              />
            </div>

            <div className={styles.separator}></div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingTitle}>
                  Упрощенная отрисовка карты
                </div>
                <div className={styles.settingSubtitle}>
                  Метки будут отображаться через HTML для совместимости
                </div>
              </div>
              <Toggle
                isActive={settings.simplifiedMapRendering}
                onClick={handleSimplifiedMapRenderingToggle}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
